<template>
  <transition name="loading-fade">
    <div v-if="isVisible" class="loading-screen"></div>
  </transition>
</template>

<script setup>
import { ref, defineExpose, onUnmounted } from "vue";
import { enableScroll } from "@/composables/useRouteLoading.js";

const isVisible = ref(false);

const show = () => {
  isVisible.value = true;
};

const hide = () => {
  isVisible.value = false;
};

// Ensure we cleanup if component is unmounted during animation
onUnmounted(() => {
  if (isVisible.value) {
    enableScroll();
  }
});

defineExpose({
  show,
  hide,
});
</script>

<style scoped>
.loading-screen {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: linear-gradient(
      to right,
      transparent calc(50% - 0.5px),
      var(--color-border-light) calc(50% - 0.5px),
      var(--color-border-light) calc(50% + 0.5px),
      transparent calc(50% + 0.5px)
    ),
    var(--color-page-initial);
  border-bottom: 1px solid var(--color-border-light);
  border-top: 1px solid var(--color-border-light);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  backdrop-filter: blur(4px);
  transform: translateY(-100%);
  animation: wipeDown 0.6s ease-out forwards, wipeUp 0.6s ease-in forwards 1.2s;
  pointer-events: none;
}

/* Content styling */
.loading-content {
  text-align: center;
  color: white;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

.loading-text {
  font-size: 16px;
  margin: 0;
  opacity: 0.9;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Split the animation into two parts for better control */
@keyframes wipeDown {
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes wipeUp {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
}

/* Vue transition handling opacity */
.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.3s ease;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}
</style>
